import { useEffect, useEffectEvent, useState } from 'react';
import type { PlaceSearchState } from './place_search/place_panel.js';

const NO_RESULTS: readonly never[] = [];

/** What the last search answered, tagged with the request (query + attempt) it
 *  answers: any other request is still loading. */
type Outcome<T> = {
  readonly request: string;
  readonly state: 'ready' | 'error';
  readonly results: readonly T[];
};

/**
 * O ciclo de uma busca remota, com debounce — a resposta de uma query velha nunca
 * sobrescreve a da mais nova: cada disparo desliga o anterior. Levantado do
 * `PlaceField` (onde nasceu para as praças e o hotel) para servir também ao menu
 * de menções do composer, que tem o mesmo ciclo em cada categoria.
 *
 * Só o DISPARO espera o debounce; a limpeza é imediata. Com `enabled` falso o
 * estado volta a `idle` na hora e os resultados somem — debounçar o termo cru
 * deixava o valor atrasado sobreviver ao fechamento: reabrir (o termo zera, o
 * debounced não) disparava a busca da vez anterior por `debounceMs`, e apagar o
 * texto deixava a lista velha no lugar das sugestões pré-digitação. É o mesmo
 * corte dos exemplos assíncronos do Base UI e do padrão de fetch da doc do React.
 * O termo VAZIO com `enabled` também dispara sem esperar — é a consulta
 * pré-digitação (recentes, roster), que não tem por que aguardar quem não digitou.
 */
export function useRemoteSearch<T>(p: {
  enabled: boolean;
  term: string;
  debounceMs: number;
  search: (term: string) => Promise<readonly T[]>;
}) {
  const live = p.term.trim();
  const [query, setQuery] = useState<string | null>(p.enabled ? live : null);
  // Closing and clearing apply in the same render; only a typed term waits.
  const immediate = p.enabled ? (live === '' ? '' : undefined) : null;
  if (immediate !== undefined && query !== immediate) setQuery(immediate);
  useEffect(() => {
    if (!p.enabled || live === '') return undefined;
    const timer = setTimeout(() => {
      setQuery(live);
    }, p.debounceMs);
    return () => {
      clearTimeout(timer);
    };
  }, [p.enabled, live, p.debounceMs]);

  const [outcome, setOutcome] = useState<Outcome<T> | null>(null);
  // Closed: the old list does not come back when the next query starts loading.
  if (query === null && outcome !== null) setOutcome(null);
  /* A busca é lida como evento, não pela identidade: um host que passe uma lambda
     inline re-dispararia a busca (e um novo render) a cada render — um laço. O
     que dispara é a CONSULTA; a função vale a mais recente na hora do disparo. */
  const search = useEffectEvent((term: string) => p.search(term));
  /* `retry` re-dispara a MESMA consulta (o erro que pede "tentar de novo"): um
     contador na requisição, sem mexer no termo que a pessoa já digitou. */
  const [attempt, setAttempt] = useState(0);
  const request = query === null ? null : JSON.stringify([query, attempt]);
  useEffect(() => {
    if (query === null) return undefined;
    const answering = JSON.stringify([query, attempt]);
    let alive = true;
    /* Dentro da cadeia: uma busca que LANÇA (síncrona, antes de devolver a promise)
       vira o estado `error` como uma rejeição — não derruba a árvore pelo effect. */
    Promise.resolve()
      .then(() => search(query))
      .then((found) => {
        if (alive) setOutcome({ request: answering, state: 'ready', results: found });
      })
      .catch(() => {
        if (alive) setOutcome({ request: answering, state: 'error', results: [] });
      });
    return () => {
      alive = false;
    };
  }, [query, attempt]);
  const retry = () => {
    setAttempt((n) => n + 1);
  };
  let state: PlaceSearchState = 'idle';
  if (request !== null) state = outcome?.request === request ? outcome.state : 'loading';
  // Loading keeps the previous answer on screen, as before the search started.
  const results = request === null ? NO_RESULTS : (outcome?.results ?? NO_RESULTS);
  return { query, state, results, retry };
}
